import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export function useAdmin() {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const hasCheckedRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);

  const checkAdminRole = useCallback(async (userId: string) => {
    try {
      const { data, error } = await (supabase as any)
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();

      if (error) {
        console.error("Error checking admin role:", error);
        setIsAdmin(false);
      } else {
        setIsAdmin(!!data);
      }
    } catch (err) {
      console.error("Error in admin check:", err);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Don't check admin status while auth is still loading
    if (authLoading) {
      setLoading(true);
      return;
    }

    // No user = not admin
    if (!user) {
      setIsAdmin(false);
      setLoading(false);
      hasCheckedRef.current = false;
      currentUserIdRef.current = null;
      return;
    }

    // If we already checked for this user, don't check again
    if (hasCheckedRef.current && currentUserIdRef.current === user.id) {
      return;
    }

    // User exists, check admin role
    setLoading(true);
    hasCheckedRef.current = true;
    currentUserIdRef.current = user.id;
    checkAdminRole(user.id);
  }, [user, authLoading, checkAdminRole]);

  // Combined loading state: still loading if auth is loading OR admin check is loading
  const combinedLoading = authLoading || loading;

  return { isAdmin, loading: combinedLoading };
}
